import Button, { ButtonVariant } from "@shared/components/ui/Button";
import React, { FormEvent, useEffect, useState } from "react";
import { Task } from "../types/task.types";

export interface TaskFormData {
  title: string;
  description?: string;
  dueDate?: Date;
}

interface TaskFormProps {
  task?: Task;
  onSubmit: (data: TaskFormData) => Promise<void>;
  onCancel: () => void;
}

export const TaskForm: React.FC<TaskFormProps> = ({
  task,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<TaskFormData>({
    title: task?.title || "",
    description: task?.description || "",
    dueDate: task?.dueDate ? task.dueDate.toDate() : undefined,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [titleError, setTitleError] = useState("");

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || "",
        dueDate: task.dueDate ? task.dueDate.toDate() : undefined,
      });
    }
  }, [task]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTitleError("");

    if (!formData.title.trim()) {
      setTitleError("Title is required");
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {/* Title */}
      <div>
        <label
          htmlFor="title"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Title
        </label>
        <input
          type="text"
          id="title"
          value={formData.title}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, title: e.target.value }))
          }
          className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-slate-800 dark:border-slate-600 ${
            titleError
              ? "border-red-500"
              : "border-gray-300 dark:border-slate-600"
          }`}
          autoFocus
        />
        {titleError && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {titleError}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Description
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, description: e.target.value }))
          }
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-slate-800 dark:border-slate-600"
        />
      </div>

      {/* Due Date */}
      <div>
        <label
          htmlFor="dueDate"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Due Date
        </label>
        <input
          type="date"
          id="dueDate"
          value={
            formData.dueDate
              ? new Date(formData.dueDate).toISOString().split("T")[0]
              : ""
          }
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              dueDate: e.target.value ? new Date(e.target.value) : undefined,
            }))
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-slate-800 dark:border-slate-600"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button
          onClick={onCancel}
          disabled={isSubmitting}
          variant={ButtonVariant.TERTIARY}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : task ? "Update Task" : "Create Task"}
        </Button>
      </div>
    </form>
  );
};
